import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components } from "@/shared/lib/api.types";
import { api, unwrap } from "@/shared/lib/apiClient";
import { overviewKeys, setupKeys, splitsKeys } from "@/shared/lib/queryKeys";

export type CharacterResponse = components["schemas"]["CharacterResponse"];
export type CreateCharacterRequest =
  components["schemas"]["CreateCharacterRequest"];
export type UpdateCharacterRequest =
  components["schemas"]["UpdateCharacterRequest"];

// Characters feed the roster (["users"]), splits, setup, and overview — invalidate all.
// The roster key (rosterKeys.users) lives in the roster feature; shared code cannot import from a
// feature, so it is spelled inline here as ["users"].
function invalidateCharacterReaders(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["users"] });
  qc.invalidateQueries({ queryKey: splitsKeys.all });
  qc.invalidateQueries({ queryKey: setupKeys.all });
  qc.invalidateQueries({ queryKey: overviewKeys.all });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCharacterRequest) =>
      unwrap(await api.POST("/api/characters", { body })),
    onSuccess: () => invalidateCharacterReaders(qc),
  });
}

export function useUpdateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCharacterRequest;
    }) =>
      unwrap(
        await api.PUT("/api/characters/{id}", {
          params: { path: { id } },
          body,
        }),
      ),
    onSuccess: () => invalidateCharacterReaders(qc),
  });
}

export function useSetMainCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/characters/{id}/set-main", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => invalidateCharacterReaders(qc),
  });
}

export function useDeleteCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE("/api/characters/{id}", { params: { path: { id } } }),
      ),
    onSuccess: () => invalidateCharacterReaders(qc),
  });
}
